// Zahlungserinnerungen einstellen.
//
// Der Schalter steht bewusst auf „aus". Hier werden Mails im Namen des
// Betriebs an dessen Kunden verschickt — das schaltet niemand für ihn ein.
// Die Vorschau zeigt vorher, wen es beim nächsten Lauf träfe.
import { useState } from 'react';
import { Erfolgsmeldung } from '../../../gemeinsam/komponenten/Meldungen';
import { api, ApiFehler } from '../../../gemeinsam/lib/api';
import { useDaten, euro, datum } from '../../../gemeinsam/lib/daten';
import { useSprache } from '../../../gemeinsam/lib/i18n';
import { Feld, Hinweis, Karte, KartenKopf, Knopf, Spinner } from '../../../gemeinsam/komponenten/Basis';

type Stand = {
  aktiv: boolean;
  tageNachFaelligkeit: number;
  wiederholungTage: number;
  maxAnzahl: number;
  versandBereit: boolean;
  firmenEmail: string;
  faellig: Array<{ nummer: string; kundeName: string; brutto: number; faelligAm: string; bisher: number }>;
};

export function Erinnerungen() {
  const { t } = useSprache();
  const { daten: stand, neuLaden } = useDaten<Stand>('/firma/erinnerungen');
  const [fehler, setFehler] = useState<string | null>(null);
  const [meldung, setMeldung] = useState<string | null>(null);
  const [sendet, setSendet] = useState(false);

  async function speichern(aenderung: Partial<Stand>) {
    if (!stand) return;
    setFehler(null);
    try {
      await api('/firma/erinnerungen', {
        methode: 'PUT',
        daten: {
          aktiv: stand.aktiv,
          tageNachFaelligkeit: stand.tageNachFaelligkeit,
          wiederholungTage: stand.wiederholungTage,
          maxAnzahl: stand.maxAnzahl,
          ...aenderung,
        },
      });
      await neuLaden();
    } catch (e) {
      setFehler(e instanceof ApiFehler ? e.message : 'Konnte nicht gespeichert werden.');
    }
  }

  async function jetztSenden() {
    if (!confirm('Die fälligen Erinnerungen jetzt verschicken?')) return;
    setSendet(true);
    setFehler(null);
    try {
      const r = await api<{ verschickt: number; fehler: number }>('/firma/erinnerungen/jetzt', { methode: 'POST' });
      setMeldung(`${r.verschickt} Erinnerung${r.verschickt === 1 ? '' : 'en'} verschickt.`
        + (r.fehler ? ` ${r.fehler} konnten nicht zugestellt werden.` : ''));
      await neuLaden();
    } catch (e) {
      setFehler(e instanceof ApiFehler ? e.message : 'Versand fehlgeschlagen.');
    } finally { setSendet(false); }
  }

  if (!stand) return <Karte><div className="flex justify-center p-8"><Spinner /></div></Karte>;

  return (
    <Karte>
      <KartenKopf titel={t('erinnerung.titel')}
                  hinweis={stand.aktiv ? t('zfa.aktiv') : t('zfa.aus')} />
      <div className="space-y-4 p-5">
        <p className="text-sm leading-relaxed text-slate-600 dark:text-slate-400">
          {t('erinnerung.erklaerung')}
        </p>

        {fehler && <Hinweis art="fehler">{fehler}</Hinweis>}
        <Erfolgsmeldung text={meldung} />
        {!stand.versandBereit && <Hinweis art="warnung">{t('zfa.keinVersand')}</Hinweis>}
        {stand.aktiv && !stand.firmenEmail && (
          <Hinweis art="warnung">{t('erinnerung.firmenmailFehlt')}</Hinweis>
        )}

        <label className="flex items-start gap-2.5 text-sm text-slate-700 dark:text-slate-300">
          <input type="checkbox" checked={stand.aktiv} disabled={!stand.versandBereit}
                 onChange={e => speichern({ aktiv: e.target.checked })}
                 className="mt-0.5 h-4 w-4 rounded border-slate-300 dark:border-slate-600" />
          <span className="font-medium">{t('erinnerung.titel')} {stand.aktiv ? t('zfa.aktiv') : t('zfa.aus')}</span>
        </label>

        {stand.aktiv && (
          <>
            <div className="grid gap-4 sm:grid-cols-3">
              <Feld label={t('erinnerung.nachTagen')} type="number" min="0" max="90"
                    value={String(stand.tageNachFaelligkeit)}
                    onChange={e => speichern({ tageNachFaelligkeit: Number(e.target.value) })} />
              <Feld label={t('erinnerung.wiederholung')} type="number" min="1" max="90"
                    value={String(stand.wiederholungTage)}
                    onChange={e => speichern({ wiederholungTage: Number(e.target.value) })} />
              <Feld label={t('erinnerung.maxAnzahl')} type="number" min="1" max="10"
                    value={String(stand.maxAnzahl)}
                    onChange={e => speichern({ maxAnzahl: Number(e.target.value) })} />
            </div>

            <div className="rounded-xl border border-slate-200 p-4 dark:border-slate-700">
              <p className="mb-2 text-sm font-medium text-slate-700 dark:text-slate-300">
                {t('erinnerung.faellig')}
              </p>
              {stand.faellig.length === 0 ? (
                <p className="text-sm text-slate-500 dark:text-slate-400">{t('erinnerung.keineFaellig')}</p>
              ) : (
                <>
                  <ul className="mb-3 divide-y divide-slate-100 text-sm dark:divide-slate-800">
                    {stand.faellig.map(f => (
                      <li key={f.nummer} className="flex flex-wrap items-center gap-2 py-2">
                        <span className="min-w-[150px] flex-1 text-slate-900 dark:text-slate-100">
                          {f.nummer} · {f.kundeName}
                        </span>
                        <span className="text-xs text-slate-500 dark:text-slate-400">
                          {datum(f.faelligAm)}{f.bisher > 0 ? ` · ${f.bisher}. Erinnerung` : ''}
                        </span>
                        <span className="font-medium text-slate-900 dark:text-slate-100">{euro(f.brutto)}</span>
                      </li>
                    ))}
                  </ul>
                  <Knopf laedt={sendet} onClick={jetztSenden}>{t('erinnerung.jetzt')}</Knopf>
                </>
              )}
            </div>
          </>
        )}
      </div>
    </Karte>
  );
}
